import {
  type ChangeEvent,
  useEffect,
  useState,
} from "react";

import { api, ApiClientError } from "../api";
import type {
  AgendaLocation,
  EffectiveAvailabilitySlot,
  Id,
  Professional,
} from "../types";
import { useToast } from "../context/ToastContext";
import { formatDateTime } from "../lib/format";

function localDateValue(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return year + "-" + month + "-" + day;
}

const APPOINTMENT_TYPES = [
  {
    code: "evaluation",
    label: "Evaluación (requiere seña)",
  },
  {
    code: "treatment",
    label: "Tratamiento",
  },
  {
    code: "follow_up",
    label: "Seguimiento",
  },
];

export default function AgendaBooking({
  opportunityId,
  serviceId,
  onBooked,
  initialProfessionalId,
  initialSlotId,
  initialDate,
  initialLocationId,
  locations = [],
}: {
  opportunityId: Id;
  serviceId: Id;
  onBooked: () => void;
  initialProfessionalId?: Id | null;
  initialSlotId?: Id | null;
  initialDate?: string | null;
  initialLocationId?: Id | null;
  locations?: AgendaLocation[];
}) {
  const { show } = useToast();

  const [professionals, setProfessionals] =
    useState<Professional[]>([]);
  const [professionalId, setProfessionalId] =
    useState(
      initialProfessionalId != null
        ? String(initialProfessionalId)
        : ""
    );
  const [appointmentType, setAppointmentType] =
    useState("evaluation");
  const [selectedDate, setSelectedDate] =
    useState(
      initialDate
        ? localDateValue(new Date(initialDate))
        : localDateValue()
    );
  const [locationId, setLocationId] =
    useState(
      initialLocationId != null
        ? String(initialLocationId)
        : ""
    );

  const [slots, setSlots] =
    useState<EffectiveAvailabilitySlot[]>([]);
  const [loadingSlots, setLoadingSlots] =
    useState(false);
  const [booking, setBooking] =
    useState<Id | null>(null);

  useEffect(() => {
    api
      .getProfessionals()
      .then((rows) =>
        setProfessionals(rows.filter((row) => row.active))
      )
      .catch(() => {
        setProfessionals([]);
        show(
          "No se pudieron cargar los profesionales.",
          "error"
        );
      });
  }, [show]);

  useEffect(() => {
    if (initialProfessionalId != null) {
      setProfessionalId(String(initialProfessionalId));
    }
  }, [initialProfessionalId]);

  useEffect(() => {
    if (!professionalId || !serviceId || !selectedDate) {
      setSlots([]);
      return;
    }

    const rangeStart =
      new Date(selectedDate + "T00:00:00");
    const rangeEnd = new Date(rangeStart);
    rangeEnd.setDate(rangeEnd.getDate() + 1);

    setLoadingSlots(true);

    Promise.all([
      api.getEffectiveAvailability({
        from: rangeStart.toISOString(),
        to: rangeEnd.toISOString(),
        service_id: serviceId,
        professional_id: professionalId as Id,
        ...(locationId
          ? { location_id: locationId as Id }
          : {}),
      }),
      api.getSlots(professionalId as Id, {
        from: rangeStart.toISOString(),
        to: rangeEnd.toISOString(),
        type: appointmentType,
      }),
    ])
      .then(([response, legacySlots]) => {
        const effectiveKeys = new Set(
          response.slots.map(
            (slot) =>
              String(slot.professional_id) +
              ":" +
              new Date(slot.starts_at).getTime()
          )
        );

        const compatibleLegacySlots =
          legacySlots
            .filter(
              (slot) =>
                slot.status === "available" &&
                (!locationId ||
                  slot.location_id == null ||
                  String(slot.location_id) === locationId)
            )
            .filter(
              (slot) =>
                !effectiveKeys.has(
                  String(slot.professional_id) +
                    ":" +
                    new Date(slot.starts_at).getTime()
                )
            )
            .map(
              (slot): EffectiveAvailabilitySlot => ({
                slot_id: slot.id,
                rule_id: null,
                professional_id: slot.professional_id,
                professional_name: "",
                specialty: null,
                service_id: serviceId,
                service_name: "",
                location_id: slot.location_id ?? null,
                starts_at: slot.starts_at,
                ends_at: slot.ends_at,
                timezone:
                  Intl.DateTimeFormat().resolvedOptions()
                    .timeZone || "UTC",
                duration_minutes: Math.round(
                  (new Date(slot.ends_at).getTime() -
                    new Date(slot.starts_at).getTime()) /
                    60000
                ),
                buffer_before_minutes: 0,
                buffer_after_minutes: 0,
                slot_interval_minutes: Math.max(
                  1,
                  Math.round(
                    (new Date(slot.ends_at).getTime() -
                      new Date(slot.starts_at).getTime()) /
                      60000
                  )
                ),
              })
            );

        setSlots(
          [...response.slots, ...compatibleLegacySlots].sort(
            (a, b) => {
              if (
                initialSlotId != null &&
                String(a.slot_id) ===
                  String(initialSlotId)
              ) {
                return -1;
              }

              if (
                initialSlotId != null &&
                String(b.slot_id) ===
                  String(initialSlotId)
              ) {
                return 1;
              }

              return (
                new Date(a.starts_at).getTime() -
                new Date(b.starts_at).getTime()
              );
            }
          )
        );
      })
      .catch(() => {
        setSlots([]);
        show(
          "No se pudieron calcular los horarios disponibles.",
          "error"
        );
      })
      .finally(() => setLoadingSlots(false));
  }, [
    appointmentType,
    initialSlotId,
    locationId,
    professionalId,
    selectedDate,
    serviceId,
    show,
  ]);

  async function book(
    slot: EffectiveAvailabilitySlot
  ) {
    const bookingKey = slot.starts_at;
    setBooking(bookingKey);

    try {
      await api.bookAppointment({
        opportunity_id: opportunityId,
        professional_id: slot.professional_id,
        appointment_type: appointmentType,
        location_id: slot.location_id ?? null,
        ...(slot.slot_id != null
          ? { slot_id: slot.slot_id }
          : { candidate_starts_at: slot.starts_at }),
      });

      show("Turno reservado correctamente.", "success");

      setSlots((current) =>
        current.filter(
          (row) => row.starts_at !== slot.starts_at
        )
      );

      onBooked();
    } catch (e) {
      if (e instanceof ApiClientError) {
        show(e.message, "error");
      } else {
        show("No se pudo reservar el turno.", "error");
      }
    } finally {
      setBooking(null);
    }
  }

  return (
    <div className="agenda-booking">
      <div className="agenda-booking-fields">
        <label>
          <span>Profesional</span>

          <select
            value={professionalId}
            onChange={(
              e: ChangeEvent<HTMLSelectElement>
            ) => setProfessionalId(e.target.value)}
          >
            <option value="">
              Elegí profesional...
            </option>

            {professionals.map((professional) => (
              <option
                key={professional.id}
                value={professional.id}
              >
                {professional.full_name}
                {professional.specialty
                  ? ` · ${professional.specialty}`
                  : ""}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Tipo de turno</span>

          <select
            value={appointmentType}
            onChange={(
              e: ChangeEvent<HTMLSelectElement>
            ) => setAppointmentType(e.target.value)}
          >
            {APPOINTMENT_TYPES.map((type) => (
              <option
                key={type.code}
                value={type.code}
              >
                {type.label}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Fecha</span>
          <input
            type="date"
            min={localDateValue()}
            value={selectedDate}
            onChange={(
              e: ChangeEvent<HTMLInputElement>
            ) => setSelectedDate(e.target.value)}
          />
        </label>

        {locations.length > 0 && (
          <label>
            <span>Sede</span>
            <select
              value={locationId}
              onChange={(
                e: ChangeEvent<HTMLSelectElement>
              ) => setLocationId(e.target.value)}
            >
              <option value="">Todas las sedes</option>
              {locations
                .filter((location) => location.active)
                .map((location) => (
                  <option
                    key={location.id}
                    value={location.id}
                  >
                    {location.name}
                  </option>
                ))}
            </select>
          </label>
        )}
      </div>

      {loadingSlots && (
        <div className="loading-strip">
          Cargando horarios disponibles...
        </div>
      )}

      {!loadingSlots &&
        professionalId &&
        slots.length === 0 && (
          <p className="meta">
            No hay horarios disponibles.
          </p>
        )}

      {slots.length > 0 && (
        <div className="agenda-booking-slots">
          {slots.map((slot) => (
            <button
              key={
                String(slot.professional_id) +
                ":" +
                slot.starts_at
              }
              className={`slot-btn ${
                initialSlotId != null &&
                String(slot.slot_id) === String(initialSlotId)
                  ? "selected"
                  : ""
              }`}
              disabled={booking === slot.starts_at}
              onClick={() => book(slot)}
            >
              {booking === slot.starts_at
                ? "Reservando..."
                : `${formatDateTime(slot.starts_at)}${
                    initialSlotId != null &&
                    String(slot.slot_id) === String(initialSlotId)
                      ? " · Horario elegido"
                      : ""
                  }`}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
